import { Component, effect, inject, Signal, signal, WritableSignal } from '@angular/core';

import { NavigationService, RouteHistoryService } from '@selpia/data-batch/history-access';
import { DimensionLevel, Perspective } from '@selpia/shared/models';
import { PerspectiveFacade } from '@selpia/perspective/data-access';
import { DataBatchFacade } from '@selpia/data-batch/data-access';
import { AppConfigFacade } from '@selpia/app-config/data-access';
import {
  DropdownComponent,
  SelectButtonComponent,
  SidebarComponent,
  SidebarToggleComponent,
  SvgSpriteIcon,
} from '@selpia/shared/ui';
import { FeatureDescriptionComponent } from '@selpia/data-batch/feature-description';
import { FeatureTabularSummaryComponent } from '@selpia/data-batch/feature-tabular-summary';
import { FeatureTrendsComponent } from '@selpia/data-batch/feature-trends';
import { FeatureDimensionBreakdownComponent } from '@selpia/data-batch/feature-dimension-breakdown';
import { FeatureTreeFiltersComponent } from '@selpia/dimension-items/feature-tree-filters';
import { OptionsFacade } from '@selpia/options/data-access';
import {MonitoringService} from '@selpia/user/tracking';

@Component({
  selector: 'web-report-view',
  standalone: true,
  imports: [
    FeatureDescriptionComponent,
    FeatureDimensionBreakdownComponent,
    FeatureTrendsComponent,
    FeatureTabularSummaryComponent,
    FeatureTreeFiltersComponent,
    DropdownComponent,
    SelectButtonComponent,
    SidebarToggleComponent,
    SidebarComponent
  ],
  templateUrl: './report-view.component.html',
  styleUrl: './report-view.component.scss',
  providers: [NavigationService],
  host: {
    '(window:resize)': 'onWindowResize()',
    '[class.opened-sidebar]': '!sidebarHidden()',
  },
})
export class ReportViewComponent {
  readonly #MOBILE_BREAKPOINT = 738;

  #mobileCheck() {
    return window.innerWidth < this.#MOBILE_BREAKPOINT;
  }

  #timeoutId: ReturnType<typeof setTimeout> | undefined;

  readonly #perspectiveFacade = inject(PerspectiveFacade);
  readonly #dataBatchFacade = inject(DataBatchFacade);
  readonly #appConfigFacade = inject(AppConfigFacade);
  readonly #optionsFacade = inject(OptionsFacade);

  readonly #navigationService = inject(NavigationService);
  readonly #routeHistoryService = inject(RouteHistoryService);

  readonly #monitoringService = inject(MonitoringService);

  protected readonly isMobile: WritableSignal<boolean> = signal(this.#mobileCheck());
  sidebarHidden: WritableSignal<boolean> = signal(true);

  constructor() {
    this.#setInitialView();

    this.#watchPerspectiveChanges();
  }

  protected readonly isLoading: Signal<boolean> = this.#appConfigFacade.isLoading;

  protected readonly availablePeriods: Signal<string[]> = this.#appConfigFacade.periods;
  protected readonly focusPeriod: Signal<string | undefined> = this.#optionsFacade.focusPeriod;

  updateFocusPeriod(period: string | undefined) {
    if (period) {
      this.#navigationService.setFocusPeriod(period);
      this.#monitoringService.logEvent('PeriodChanged', { period: period, view: 'Report' });
    }
  }

  #watchPerspectiveChanges(): void {
    effect(
      () => {
        const perspective: Perspective | undefined = this.#perspectiveFacade.perspective();

        if (perspective) {
          this.#dataBatchFacade.loadDataBatch(perspective);
        }
        this.#monitoringService.logEvent(`Perspective changes`, {dimensionName: perspective?.focusDimensionLevel.name, view: 'Report'});
      },
    );
  }

  #setInitialView(): void {
    const e1 = effect(
      () => {
        const topLevel: DimensionLevel | undefined = this.#appConfigFacade.dimensions()
          ?.at(0)
          ?.levels
          .at(0);

        if (!topLevel) {
          return;
        }

        const measure = this.#appConfigFacade.initialFocusMeasure();
        const period = this.#appConfigFacade.initialPeriod();
        const date = this.#appConfigFacade.initialDate();

        if (period && date && measure) {
          this.#navigationService.initializeView({
            focusDimensionLevel: topLevel,
            filterDimensions: [],
            includeTotal: true,
            date,
          },
            { measure, period },
          );
          e1.destroy();
        }
      },
      { manualCleanup: true },
    );
    this.#monitoringService.logPageView('Report', '/report');
  }

  onBack() {
    this.#routeHistoryService.goBack();
    this.#monitoringService.logTrace('Back report view');
  }

  protected readonly previousUrl = this.#routeHistoryService.previousUrl();

  onWindowResize(): void {
    clearTimeout(this.#timeoutId);
    this.#timeoutId = setTimeout(() => {
      this.isMobile.set(this.#mobileCheck());
    }, 50);
  }

  protected readonly SvgSpriteIcon = SvgSpriteIcon;
}


